import create from 'zustand'
import type { StateCreator } from 'zustand'
import { ThreeEvent } from '@react-three/fiber'

export type EventTypes =
  | 'position'
  | 'vertical_transmitter'
  | 'tensile_transmitter'
  | 'opening'
  | 'ifc_glb_model'

export type EventData = {
  type: EventTypes
}

export interface PositionClickData extends EventData {
  type: 'position'
}

export interface VerticalTransmitterClickData extends EventData {
  type: 'vertical_transmitter'
  elementGuid: string
  transmitterGuid: string
}

export interface TensileTransmitterClickData extends EventData {
  type: 'tensile_transmitter'
  elementGuid: string
  transmitterGuid: string
}

export interface IFCGlbModelClickData extends EventData {
  type: 'ifc_glb_model'
}

export interface OpeningClickData extends EventData {
  type: 'opening'
  wallGuid: string
}

export type ModelMeshEventData =
  | PositionClickData
  | VerticalTransmitterClickData
  | TensileTransmitterClickData
  | OpeningClickData
  | IFCGlbModelClickData

export type ModelMeshClickListener = (
  guid: string,
  event: ThreeEvent<MouseEvent>,
  data: ModelMeshEventData,
) => void

type ModelMeshStoreState = {
  selectedGuids: Map<string, string[]>
  addSelectedGuids: (guid: string, selectedGuids: string[]) => void
  removeSelectedGuids: (guid: string) => void

  hiddenGuids: Map<string, string[]>
  addHiddenGuids: (guid: string, hiddenGuids: string[]) => void
  removeHiddenGuids: (guid: string) => void

  visibleTensileTransmitterElementGuids: Map<string, string[]>
  addVisibleTensileTransmitterElementGuids: (guid: string, elementGuids: string[]) => void
  removeVisibleTensileTransmitterElementGuids: (guid: string) => void

  visibleVerticalTransmitterElementGuids: Map<string, string[]>
  addVisibleVerticalTransmitterElementGuids: (guid: string, elementGuids: string[]) => void
  removeVisibleVerticalTransmitterElementGuids: (guid: string) => void
  selectedCheckPositions: Map<string, { positionGuid: string; checkPosition: number }>
  addSelectedCheckPositions: (guid: string, positionGuid: string, checkPosition: number) => void
  removeSelectedCheckPositions: (guid: string) => void

  highlightedPositions: Map<string, string[]>
  addHighlightedPositions: (positionGuid: string, highlightedPositions: string[]) => void
  removeHighlightedPositions: (positionGuid: string) => void

  targetPositions: Map<string, string[]>
  addTargetPositions: (positionGuid: string, targetPositions: string[]) => void
  removeTargetPositions: (positionGuid: string) => void
}

const creator: StateCreator<ModelMeshStoreState> = set => ({
  hiddenGuids: new Map<string, string[]>(),
  selectedGuids: new Map<string, string[]>(),
  visibleTensileTransmitterElementGuids: new Map<string, string[]>(),
  visibleVerticalTransmitterElementGuids: new Map<string, string[]>(),

  selectedCheckPositions: new Map<string, { positionGuid: string; checkPosition: number }>(),
  highlightedPositions: new Map<string, string[]>(),
  targetPositions: new Map<string, string[]>(),
  addSelectedGuids: (guid: string, selectedGuids: string[]) => {
    set((state: ModelMeshStoreState) => {
      const selectedGuidsMap = new Map(state.selectedGuids)
      selectedGuidsMap.set(guid, selectedGuids)
      return { selectedGuids: selectedGuidsMap }
    })
  },

  removeSelectedGuids: (guid: string) => {
    set((state: ModelMeshStoreState) => {
      const selectedGuids = new Map(state.selectedGuids)
      selectedGuids.delete(guid)
      return { selectedGuids }
    })
  },

  addHiddenGuids: (guid: string, hiddenGuids: string[]) => {
    set((state: ModelMeshStoreState) => {
      const hiddenGuidsMap = new Map(state.hiddenGuids)
      hiddenGuidsMap.set(guid, hiddenGuids)
      return { hiddenGuids: hiddenGuidsMap }
    })
  },

  removeHiddenGuids: (guid: string) => {
    set((state: ModelMeshStoreState) => {
      const hiddenGuids = new Map(state.hiddenGuids)
      hiddenGuids.delete(guid)
      return { hiddenGuids }
    })
  },

  addVisibleTensileTransmitterElementGuids: (guid: string, elementGuids: string[]) => {
    set((state: ModelMeshStoreState) => {
      const visibleTensileTransmitterElementGuids = new Map(
        state.visibleTensileTransmitterElementGuids,
      )
      visibleTensileTransmitterElementGuids.set(guid, elementGuids)
      return {
        visibleTensileTransmitterElementGuids,
      }
    })
  },

  removeVisibleTensileTransmitterElementGuids: (guid: string) => {
    set((state: ModelMeshStoreState) => {
      const visibleTensileTransmitterElementGuids = new Map(
        state.visibleTensileTransmitterElementGuids,
      )
      visibleTensileTransmitterElementGuids.delete(guid)
      return {
        visibleTensileTransmitterElementGuids,
      }
    })
  },

  addVisibleVerticalTransmitterElementGuids: (guid: string, elementGuids: string[]) => {
    set((state: ModelMeshStoreState) => {
      const visibleVerticalTransmitterElementGuids = new Map(
        state.visibleVerticalTransmitterElementGuids,
      )
      visibleVerticalTransmitterElementGuids.set(guid, elementGuids)
      return {
        visibleVerticalTransmitterElementGuids,
      }
    })
  },

  removeVisibleVerticalTransmitterElementGuids: (guid: string) => {
    set((state: ModelMeshStoreState) => {
      const visibleVerticalTransmitterElementGuids = new Map(
        state.visibleVerticalTransmitterElementGuids,
      )
      visibleVerticalTransmitterElementGuids.delete(guid)
      return {
        visibleVerticalTransmitterElementGuids,
      }
    })
  },

  addSelectedCheckPositions: (guid: string, positionGuid: string, checkPosition: number) => {
    set((state: ModelMeshStoreState) => {
      const selectedCheckPositions = new Map(state.selectedCheckPositions)
      selectedCheckPositions.set(guid, { positionGuid, checkPosition })
      return { selectedCheckPositions }
    })
  },

  removeSelectedCheckPositions: (guid: string) => {
    set((state: ModelMeshStoreState) => {
      const selectedCheckPositions = new Map(state.selectedCheckPositions)
      selectedCheckPositions.delete(guid)
      return { selectedCheckPositions }
    })
  },

  addHighlightedPositions: (positionGuid: string, highlightedPositions: string[]) => {
    set((state: ModelMeshStoreState) => {
      const highlightedPositionsMap = new Map(state.highlightedPositions)
      highlightedPositionsMap.set(positionGuid, highlightedPositions)
      return { highlightedPositions: highlightedPositionsMap }
    })
  },

  removeHighlightedPositions: (positionGuid: string) => {
    set((state: ModelMeshStoreState) => {
      const highlightedPositions = new Map(state.highlightedPositions)
      highlightedPositions.delete(positionGuid)
      return { highlightedPositions }
    })
  },

  addTargetPositions: (positionGuid: string, targetPositions: string[]) => {
    set((state: ModelMeshStoreState) => {
      const targetPositionsMap = new Map(state.targetPositions)
      targetPositionsMap.set(positionGuid, targetPositions)
      return { targetPositions: targetPositionsMap }
    })
  },

  removeTargetPositions: (positionGuid: string) => {
    set((state: ModelMeshStoreState) => {
      const targetPositions = new Map(state.targetPositions)
      targetPositions.delete(positionGuid)
      return { targetPositions }
    })
  },
})

export const useModelMeshStore = create<ModelMeshStoreState>(creator)
